import { LayerKey, type WorkspaceKey } from '@festina-life/api'
import { assertNotEmpty } from '@festina-life/core'
import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router'

import { DraftCreationModal } from '../components/draftCreationModal/DraftCreationModal'
import type { DraftIdentifier } from '../components/draftCreationModal/types'
import { useBaseBranchWorkspaces } from '../hooks/useBaseBranchWorkspaces'
import { configurationItem } from '../system/paths'
import { useNavigationParams } from '../system/useNavigationParams'
import { useWorkspaceManagerContext } from '../workspace/workspaceManagerContext'
import { Provider } from './draftContext'
import type { DraftInitialValues } from './types'

type Props = {
  children: ReactNode
}

/**
 * Context provider for controlling draft creation and editing.
 */
const DraftProvider = ({ children }: Props) => {
  const [initialValue, setInitialValue] = useState<DraftInitialValues>()
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false)
  const navigate = useNavigate()
  const { server, branch, layer } = useNavigationParams()
  const [draftLayer, setDraftLayer] = useState<LayerKey>(new LayerKey(layer))
  const { onOpenWorkspaceSelector, currentWorkspaceKey, onSwitch } =
    useWorkspaceManagerContext()
  const branchWorkspaces = useBaseBranchWorkspaces({
    name: branch ?? '',
    server,
  })
  const isSingleWorkspaceBranch = branchWorkspaces.length === 1
  const hasNoWorkspaces = branchWorkspaces.length === 0

  const handleStartDraftCreation = async (
    input: DraftInitialValues,
    layerKey: LayerKey,
  ) => {
    assertNotEmpty(branch)
    setInitialValue(input)
    setDraftLayer(layerKey)
    setIsDraftModalOpen(true)

    if (currentWorkspaceKey) {
      return
    }

    const workspaceBranchKey = { name: branch, server }

    try {
      // if (hasNoWorkspaces) {
      // Delay workspace creation until after the user confirms draft creation, to avoid cluttering the workspace list with unused workspaces
      // Workspace creation is done in the OperationsProvider for branches without workspaces.
      // }

      if (isSingleWorkspaceBranch) {
        const workspaceName = branchWorkspaces[0].name
        assertNotEmpty(workspaceName)
        await onSwitch(
          { name: workspaceName, branchKey: workspaceBranchKey },
          { mode: 'preserve' },
        )
      } else if (branchWorkspaces.length > 1) {
        onOpenWorkspaceSelector(workspaceBranchKey)
      }
    } catch {
      setInitialValue(undefined)
      setDraftLayer(layer)
      setIsDraftModalOpen(false)
    }
  }

  const handleDraftCreation = async (
    createdDraft: DraftIdentifier,
    addedToWorkspaceKey: WorkspaceKey,
  ) => {
    const commonConfigurationItemProps = {
      workspaceName: addedToWorkspaceKey.name,
      server: addedToWorkspaceKey.branchKey.server,
      branch: addedToWorkspaceKey.branchKey.name,
      layer: createdDraft.layer,
      block: createdDraft.block,
    }

    await navigate(
      configurationItem({
        ...commonConfigurationItemProps,
        itemId: createdDraft.name,
        type: createdDraft.type,
      }),
    )

    setInitialValue(undefined)
    setDraftLayer(layer)
    setIsDraftModalOpen(false)
  }

  return (
    <Provider
      value={{
        onConfigureNewDraft: handleStartDraftCreation,
      }}
    >
      <DraftCreationModal
        onSuccess={handleDraftCreation}
        onCancel={() => {
          setInitialValue(undefined)
          setDraftLayer(layer)
          setIsDraftModalOpen(false)
        }}
        isOpen={isDraftModalOpen && (!!currentWorkspaceKey || hasNoWorkspaces)}
        initialValue={initialValue}
        layer={draftLayer}
      />
      {children}
    </Provider>
  )
}

export { DraftProvider }
